import React, { 
  useRef, 
  useEffect, 
  useLayoutEffect, 
  useState, 
  useCallback, 
  useImperativeHandle,
  forwardRef,
  createContext,
  useContext 
} from 'react';
import { BarData, ChartIndicatorData } from '@/types';
import { cn } from '@/lib/utils';
import { attachOlderBarLoader, captureVisibleRange, restoreVisibleRange, type PagingHandle } from '@/components/modern/chartPaging';
import SimpleChart from './simple-chart';

// Define types locally to avoid import issues
type Time = number;

interface CandlestickData {
  time: Time;
  open: number;
  high: number;
  low: number;
  close: number;
}

interface LineData {
  time: Time;
  value: number;
}

interface ChartApi {
  api: () => any;
  free: () => void;
  /** Series call this after applying data so paging can re-evaluate the range. */
  dataApplied?: () => void;
}

interface SeriesApi {
  api: () => any;
  free: () => void;
}

// Create contexts for chart and series references
const ChartContext = createContext<ChartApi | null>(null);
const SeriesContext = createContext<SeriesApi | null>(null);

interface ChartContainerProps {
  children?: React.ReactNode;
  className?: string;
  height?: number;
  onCrosshairMove?: (time: Time | undefined, price: number | undefined) => void;
  /** Pan-back paging: called when the viewport nears the oldest loaded bar.
   *  Resolve with the number of bars prepended; 0 stops further requests. */
  onLoadOlder?: () => Promise<number>;
  /** Bump to clear the "no more history" latch — e.g. on symbol/timeframe change. */
  pagingResetKey?: string | number;
}

// Main chart container component following the official pattern
export const ChartContainer = forwardRef<any, ChartContainerProps>(({
  children,
  className,
  height = 450,
  onCrosshairMove,
  onLoadOlder,
  pagingResetKey
}, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  // Paging is attached from inside chart creation rather than from an effect:
  // the chart is built after an async import, so an effect would find the ref
  // empty, and signalling readiness through state re-entered via free()/api()
  // and blew the render depth.
  const loadOlderRef = useRef<(() => Promise<number>) | undefined>(undefined);
  const pagingRef = useRef<PagingHandle | null>(null);
  const initializingRef = useRef<boolean>(false);

  const chartApiRef = useRef<ChartApi>({
    dataApplied() {
      pagingRef.current?.recheck();
    },
    api() {
      // Return existing chart if available
      if (chartRef.current) {
        return chartRef.current;
      }

      // Check if container exists and is not already initializing
      if (!containerRef.current || initializingRef.current) {
        return null;
      }

      // Check if container already has a canvas element (chart already exists)
      const existingCanvas = containerRef.current.querySelector('canvas');
      if (existingCanvas) {
        console.warn('Chart canvas already exists in container, skipping creation');
        return null;
      }

      // Mark as initializing to prevent concurrent creation
      initializingRef.current = true;

      // Create chart synchronously if possible, or async if module needs to be imported
      import('lightweight-charts').then(({ createChart, ColorType }) => {
        // Double-check conditions after async import
        if (!containerRef.current || chartRef.current) {
          initializingRef.current = false;
          return;
        }

        // Final check for existing canvas after async operation
        const existingCanvasAfterImport = containerRef.current.querySelector('canvas');
        if (existingCanvasAfterImport) {
          console.warn('Chart canvas created during import, skipping duplicate creation');
          initializingRef.current = false;
          return;
        }

        const isDark = document.documentElement.classList.contains('dark');
        
        const chart = createChart(containerRef.current, {
          autoSize: false,
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight,
          layout: {
            background: { type: ColorType.Solid, color: 'transparent' },
            textColor: isDark ? '#d1d4dc' : '#333',
          },
          rightPriceScale: {
            borderColor: isDark ? 'rgba(197, 203, 206, 0.4)' : '#ccc',
            visible: true,
            borderVisible: false,
          },
          timeScale: {
            borderColor: isDark ? 'rgba(197, 203, 206, 0.4)' : '#ccc',
            visible: true,
            borderVisible: false,
            timeVisible: true,
            secondsVisible: false,
          },
          grid: {
            vertLines: {
              color: isDark ? 'rgba(197, 203, 206, 0.1)' : 'rgba(197, 203, 206, 0.2)',
            },
            horzLines: {
              color: isDark ? 'rgba(197, 203, 206, 0.1)' : 'rgba(197, 203, 206, 0.2)',
            },
          },
          crosshair: {
            mode: 1,
          },
          // Explicitly enable chart interactions
          handleScroll: true,
          handleScale: true,
        });

        chartRef.current = chart;
        initializingRef.current = false;

        // Setup crosshair move handler if provided
        if (onCrosshairMove) {
          chart.subscribeCrosshairMove((param: any) => {
            if (param.time && param.seriesData?.size > 0) {
              const data = Array.from(param.seriesData.values())[0] as any;
              onCrosshairMove(param.time as Time, data?.value || data?.close);
            } else {
              onCrosshairMove(undefined, undefined);
            }
          });
        }

        // Fit content after creation
        setTimeout(() => {
          if (chartRef.current) {
            chartRef.current.timeScale().fitContent();
          }
        }, 0);
      }).catch(error => {
        console.error('Failed to import lightweight-charts:', error);
        initializingRef.current = false;
      });

      return null;
    },
    free() {
      if (chartRef.current) {
        try {
          chartRef.current.remove();
        } catch (error) {
          console.warn('Error removing chart:', error);
        }
        chartRef.current = null;
      }
      pagingRef.current?.detach();
      pagingRef.current = null;
      initializingRef.current = false;
    }
  });

  // Setup chart and resize observer
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Initialize chart once
    chartApiRef.current.api();

    // Setup resize observer
    resizeObserverRef.current = new ResizeObserver(entries => {
      if (entries.length === 0 || !chartRef.current) return;
      
      const { width, height } = entries[0].contentRect;
      chartRef.current.applyOptions({ width, height });
    });

    resizeObserverRef.current.observe(container);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      chartApiRef.current.free();
    };
  }, []);

  // Keep the loader current without re-running the attachment.
  loadOlderRef.current = onLoadOlder;

  // Attach pan-back paging. The chart is built after an async import, so the ref
  // is usually still empty when this first runs — hence the rAF retry rather
  // than a readiness flag (state here re-entered through free()/api() and blew
  // the render depth). Deps are stable primitives, so this runs once per series.
  useEffect(() => {
    if (!onLoadOlder) return;
    // Watch the chart *identity* rather than attaching once. The instance is
    // created after an async import, and free()/api() can replace it (React's
    // double-invoked effects in development do exactly that) — attaching a
    // single time reliably ends up bound to a chart that was thrown away.
    let attachedTo: any = null;
    const sync = () => {
      const chart = chartRef.current;
      if (chart === attachedTo) return;
      pagingRef.current?.detach();
      pagingRef.current = chart
        ? attachOlderBarLoader(chart, {
            onLoadOlder: () => (loadOlderRef.current?.() ?? Promise.resolve(0)),
          })
        : null;
      attachedTo = chart;
    };
    sync();
    const id = setInterval(sync, 300);
    return () => {
      clearInterval(id);
      pagingRef.current?.detach();
      pagingRef.current = null;
    };
  }, [!!onLoadOlder, pagingResetKey]);

  // Expose chart API via ref
  useImperativeHandle(ref, () => ({
    chart: () => chartRef.current,
    container: () => containerRef.current,
  }), []);

  return (
    <ChartContext.Provider value={chartApiRef.current}>
      <div 
        ref={containerRef}
        className={cn("w-full", className)}
        style={{ height: `${height}px` }}
      />
      {children}
    </ChartContext.Provider>
  );
});

ChartContainer.displayName = 'ChartContainer';

interface SeriesProps {
  type: 'candlestick' | 'line' | 'area';
  data: CandlestickData[] | LineData[];
  options?: any;
  children?: React.ReactNode;
}

// Series component following the official pattern
export const Series = forwardRef<any, SeriesProps>(({
  type,
  data,
  options = {},
  children
}, ref) => {
  const parent = useContext(ChartContext);
  const seriesRef = useRef<any>(null);
  
  // Memoize options to prevent unnecessary recreations
  const memoizedOptions = React.useMemo(() => options, [JSON.stringify(options)]);

  const seriesApiRef = useRef<SeriesApi>({
    async api() {
      // Return existing series if available
      if (seriesRef.current) {
        return seriesRef.current;
      }

      // Check if parent chart context is available
      if (!parent) {
        return null;
      }

      // Wait for chart to be available (non-blocking)
      let chart = parent.api();
      if (!chart) {
        // Instead of blocking, schedule a retry
        setTimeout(async () => {
          const retryChart = parent.api();
          if (retryChart && !seriesRef.current) {
            // Try to create series again if chart is now available
            await seriesApiRef.current.api();
          }
        }, 100);
        return null;
      }

      try {
        let series;
        
        if (type === 'candlestick') {
          const { CandlestickSeries } = await import('lightweight-charts');
          series = chart.addSeries(CandlestickSeries, {
            upColor: '#26a69a',
            downColor: '#ef5350',
            wickUpColor: '#26a69a',
            wickDownColor: '#ef5350',
            ...memoizedOptions
          });
        } else if (type === 'line') {
          const { LineSeries } = await import('lightweight-charts');
          series = chart.addSeries(LineSeries, {
            color: memoizedOptions?.color || '#2962FF',
            lineWidth: 2,
            ...memoizedOptions
          });
        } else if (type === 'area') {
          const { AreaSeries } = await import('lightweight-charts');
          series = chart.addSeries(AreaSeries, {
            topColor: memoizedOptions?.topColor || 'rgba(41, 98, 255, 0.5)',
            bottomColor: memoizedOptions?.bottomColor || 'rgba(41, 98, 255, 0.04)',
            lineColor: memoizedOptions?.lineColor || 'rgba(41, 98, 255, 1)',
            lineWidth: 2,
            ...memoizedOptions
          });
        }

        seriesRef.current = series;
      } catch (error) {
        console.error(`Error creating ${type} series:`, error);
      }

      return seriesRef.current;
    },
    free() {
      if (seriesRef.current && parent) {
        const chart = parent.api();
        if (chart) {
          try {
            chart.removeSeries(seriesRef.current);
          } catch (error) {
            console.log('Series already removed');
          }
        }
        seriesRef.current = null;
      }
    }
  });
  
  // Initialize series only - data is handled separately
  useLayoutEffect(() => {
    const initSeries = async () => {
      await seriesApiRef.current.api();
      // Don't set data here - let the data effect handle it
    };

    initSeries();

    return () => {
      isInitialDataSet.current = false;
      seriesApiRef.current.free();
    };
  }, [type]); // Only recreate if series type changes, not options

  // Handle data updates - following TradingView real-time example pattern
  const isInitialDataSet = useRef(false);
  const lastBarTime = useRef<number>(0);
  // Oldest bar currently plotted. The realtime path below only ever hands the chart its LAST bar,
  // so bars inserted BEFORE the plotted range (a history top-up) would never reach the series —
  // React state and the bar counter would show the full history while the chart still drew the
  // thin live response. Tracking the leading edge is what lets us tell a prepend from a tick.
  const firstBarTime = useRef<number>(0);
  // Previous series length, used to tell a first history top-up (one live candle on screen, so
  // refit) from a genuine pan-back prepend (a real viewport worth holding still).
  const prevLen = useRef(0);
  
  useEffect(() => {
    if (!seriesRef.current || !data || data.length === 0) return;
    
    // Set initial data once
    if (!isInitialDataSet.current) {
      console.debug('[Series] Initial data:', data.length, 'bars');
      seriesRef.current.setData(data);
      isInitialDataSet.current = true;
      prevLen.current = data.length;
      if (data.length > 0) {
        lastBarTime.current = data[data.length - 1].time;
        firstBarTime.current = data[0].time;
      }
      
      // Opening viewport.
      //
      // fitContent() squeezes the ENTIRE series into the available width. That is
      // fine for a handful of bars, but a 200-bar history top-up in a pane a few
      // hundred pixels wide renders candles about a pixel across - the chart "opens
      // zoomed out to nothing". Show a readable window of the most recent bars
      // instead, keeping a little room on the right for the bar currently building.
      const chart = parent?.api();
      if (chart) {
        const OPENING_BARS = 90;
        if (data.length > OPENING_BARS) {
          chart.timeScale().setVisibleLogicalRange({
            from: data.length - OPENING_BARS,
            to: data.length + 5,
          });
        } else {
          chart.timeScale().fitContent();
          // Leave room for updates.
          chart.timeScale().scrollToPosition(5);
        }
      }
      parent?.dataApplied?.();
      return;
    }

    // Older bars were prepended (pan-back paging). The steady-state path below
    // only ever `update()`s the newest bar, so a prepend needs a full setData —
    // and setData resets the viewport, so the visible span is captured first and
    // restored after. Restoring by TIME rather than logical index is what makes
    // this stable: prepending shifts every index, but the times the user is
    // looking at do not move.
    if (data[0].time !== firstBarTime.current) {
      // Earlier first bar = a prepend, so hold the viewport still. Any other
      // change means the series was replaced wholesale (timeframe switch), which
      // should refit — previously that case fell through to the update() path
      // below and left the old timeframe's candles on screen.
      // Only hold the viewport when there was a real viewport to hold. The first
      // history top-up lands on a chart showing a single live candle; restoring
      // that one-bar span would leave the user zoomed onto the right edge of a
      // series that just gained 200 bars, so that case refits instead.
      const isPrepend = data[0].time < firstBarTime.current && prevLen.current >= 10;
      const chart = parent?.api();
      const keep = isPrepend ? captureVisibleRange(chart) : null;
      console.debug(isPrepend ? '[Series] Prepended history:' : '[Series] Series replaced:',
        data.length, 'bars total');
      seriesRef.current.setData(data);
      firstBarTime.current = data[0].time;
      lastBarTime.current = data[data.length - 1].time;
      prevLen.current = data.length;
      if (isPrepend) restoreVisibleRange(chart, keep);
      else chart?.timeScale?.().fitContent();
      parent?.dataApplied?.();
      return;
    }
    
    // History prepended (drawer top-up, pan-back): the array now starts earlier than what's
    // plotted. update() can only append or amend the newest bar, so reload the whole series —
    // otherwise the older candles stay invisible no matter how many arrive.
    if (data[0].time < firstBarTime.current) {
      console.debug('[Series] History prepended:', data.length, 'bars; reloading series');
      seriesRef.current.setData(data);
      firstBarTime.current = data[0].time;
      lastBarTime.current = data[data.length - 1].time;
      return;
    }

    // Handle real-time updates - following the chart-realtime.md pattern
    const lastBar = data[data.length - 1];
    const lastBarCandle = lastBar as CandlestickData;

    // Log the update call with detailed info about what triggered it
    const updateInfo = {
      time: lastBar.time,
      isNewBar: lastBar.time > lastBarTime.current,
      isSameBar: lastBar.time === lastBarTime.current,
      dataLength: data.length,
      lastBarOHLC: 'open' in lastBar ? {
        open: lastBarCandle.open,
        high: lastBarCandle.high,
        low: lastBarCandle.low,
        close: lastBarCandle.close
      } : undefined,
    };
    
    console.debug('[TradingView Chart] update:', updateInfo);
    
    // Update the series with the latest bar data
    seriesRef.current.update(lastBar);
    
    // Track if this is a new bar
    if (lastBar.time > lastBarTime.current) {
      console.debug('[TradingView Chart] New bar:', lastBar.time);
      lastBarTime.current = lastBar.time;
    }
  }, [data]);

  // Expose series API via ref
  useImperativeHandle(ref, () => ({
    series: () => seriesRef.current,
  }), []);

  return (
    <SeriesContext.Provider value={seriesApiRef.current}>
      {children}
    </SeriesContext.Provider>
  );
});

Series.displayName = 'Series';

// Main TradingViewChart component using the new pattern
interface TradingViewChartProps {
  data: BarData[];
  symbol: string;
  className?: string;
  height?: number;
  showVolume?: boolean;
  indicators?: {
    name: string;
    type: 'sma' | 'ema' | 'rsi' | 'macd' | 'bollinger';
    parameters: { [key: string]: any };
    color: string;
  }[];
  /** Server-calculated indicator data from backend API or SignalR */
  serverIndicators?: Record<string, ChartIndicatorData>;
  onCrosshairMove?: (time: Time | undefined, price: number | undefined) => void;
  /** Pan-back paging: fetch + prepend the next older page, resolving with how
   *  many bars were added (0 = no more history). */
  onLoadOlder?: () => Promise<number>;
  /** Identity of the current series — changing it clears the "no more history"
   *  latch. Pass something like `${symbol}_${timeframe}`. */
  pagingResetKey?: string | number;
}

// Convert server indicator data to LineData for chart rendering
function serverIndicatorToLineData(indicator: ChartIndicatorData): LineData[] {
  return indicator.values
    .filter(v => v.timestamp)
    .map(v => ({
      time: Math.floor(new Date(v.timestamp).getTime() / 1000) as Time,
      value: v.value,
    }))
    .sort((a, b) => a.time - b.time);
}

// Extract sub-series from indicator metadata (e.g. BB upper/lower bands, MACD signal/histogram)
function serverIndicatorMetaSeries(indicator: ChartIndicatorData, metaKey: string): LineData[] {
  return indicator.values
    .filter(v => v.timestamp && v.metadata?.[metaKey] != null)
    .map(v => ({
      time: Math.floor(new Date(v.timestamp).getTime() / 1000) as Time,
      value: Number(v.metadata[metaKey]),
    }))
    .sort((a, b) => a.time - b.time);
}

// Default colors for server indicators
const SERVER_INDICATOR_COLORS: Record<string, string> = {
  SMA: '#2196F3',
  RSI: '#FF9800',
  BB: '#9C27B0',
  MACD: '#4CAF50',
};

export const TradingViewChart: React.FC<TradingViewChartProps> = ({
  data,
  symbol,
  className,
  height = 450,
  showVolume = false,
  indicators = [],
  serverIndicators,
  onCrosshairMove,
  onLoadOlder,
  pagingResetKey
}) => {
  const chartRef = useRef<any>(null);
  const [chartError, setChartError] = useState<string | null>(null);
  const [useFallback, setUseFallback] = useState(false);
  const [showRealtimeButton, setShowRealtimeButton] = useState(false);

  // Convert BarData to TradingView format
  const convertToTradingViewData = useCallback((bars: BarData[]): CandlestickData[] => {
    return bars
      .filter(bar => {
        // Filter out invalid bars
        if (!bar || !bar.barStartTime) return false;
        // Check if we have OHLC values (either from backend or calculated)
        const hasOHLC = (bar.open !== undefined && bar.high !== undefined && 
                        bar.low !== undefined && bar.close !== undefined);
        if (!hasOHLC) {
          console.warn('[TradingViewChart] Bar missing OHLC values:', bar);
          return false;
        }
        return true;
      })
      .map((bar) => {
        const timestamp = Math.floor(new Date(bar.barStartTime).getTime() / 1000) as Time;
        return {
          time: timestamp,
          open: bar.open!,
          high: bar.high!,
          low: bar.low!,
          close: bar.close!,
        };
      })
      .sort((a, b) => a.time - b.time);
  }, []);

  // Calculate indicator data
  const calculateIndicatorData = useCallback((
    bars: BarData[],
    indicator: typeof indicators[0]
  ): LineData[] => {
    switch (indicator.type) {
      case 'sma':
      case 'ema':
        return calculateMovingAverage(bars, indicator.parameters.period, indicator.type === 'ema');
      case 'rsi':
        return calculateRSI(bars, indicator.parameters.period);
      default:
        return [];
    }
  }, []);

  const candlestickData = convertToTradingViewData(data);
  
  // Remove the duplicate update logic since Series component handles it

  // Use fallback if there's an error
  if (useFallback || chartError) {
    return (
      <SimpleChart
        data={data}
        symbol={symbol}
        className={className}
        height={height}
        showVolume={showVolume}
        indicators={indicators}
        onCrosshairMove={onCrosshairMove}
      />
    );
  }

  // Handle "Go to realtime" button following chart-realtime.md pattern
  const handleGoToRealtime = useCallback(() => {
    const chart = chartRef.current?.chart();
    if (chart) {
      chart.timeScale().scrollToRealTime();
      setShowRealtimeButton(false);
    }
  }, []);

  // Check if user has scrolled away from realtime
  useEffect(() => {
    const chart = chartRef.current?.chart();
    if (!chart) return;

    const handleVisibleRangeChange = () => {
      const timeScale = chart.timeScale();
      const visibleRange = timeScale.getVisibleRange();
      if (!visibleRange || candlestickData.length === 0) return;

      const lastBarTime = candlestickData[candlestickData.length - 1].time;
      const prevBarTime = candlestickData.length > 1
        ? candlestickData[candlestickData.length - 2].time
        : undefined;

      // This series is built with UNIX-second stamps, but Time can also be a
      // BusinessDay object - bail rather than compare one numerically.
      if (typeof lastBarTime !== 'number' || typeof visibleRange.to !== 'number') {
        setShowRealtimeButton(false);
        return;
      }

      // "Scrolled away from realtime" has to be measured in BARS, not in a fixed
      // number of seconds.
      //
      // At a hardcoded 120s the test was meaningless on any timeframe coarser than a
      // minute: sitting exactly at the right edge of a daily chart still leaves
      // `to` a full bar - 86,400s - behind the last bar's stamp, so the condition
      // stayed true. Clicking the button hid it, scrollToRealTime() had nowhere to
      // go, this handler fired again and brought it straight back. The button looked
      // like it did nothing, because visibly it did.
      const spacing = typeof prevBarTime === 'number' && lastBarTime > prevBarTime
        ? lastBarTime - prevBarTime
        : 60;
      setShowRealtimeButton(visibleRange.to < lastBarTime - spacing * 2);
    };

    chart.timeScale().subscribeVisibleTimeRangeChange(handleVisibleRangeChange);
    
    return () => {
      try {
        chart.timeScale().unsubscribeVisibleTimeRangeChange(handleVisibleRangeChange);
      } catch (e) {
        // Chart might be removed
      }
    };
  }, [candlestickData]);

  return (
    <div className="relative">
      <ChartContainer
        ref={chartRef}
        className={className}
        height={height}
        onCrosshairMove={onCrosshairMove}
        onLoadOlder={onLoadOlder}
        pagingResetKey={pagingResetKey}
      >
        {candlestickData.length > 0 && (
          <Series
            type="candlestick"
            data={candlestickData}
          />
        )}
        
        {indicators.map((indicator, index) => {
          const indicatorData = calculateIndicatorData(data, indicator);
          if (indicatorData.length === 0) return null;
          
          return (
            <Series
              key={`local_${indicator.name}_${index}`}
              type="line"
              data={indicatorData}
              options={{
                color: indicator.color,
                title: `${indicator.name.toUpperCase()}(${Object.values(indicator.parameters).join(', ')})`,
                priceScaleId: indicator.type === 'rsi' ? 'rsi' : undefined,
              }}
            />
          );
        })}

        {/* Server-calculated indicators from backend */}
        {serverIndicators && Object.entries(serverIndicators).map(([name, indicatorData]) => {
          const upperName = name.toUpperCase();
          const mainData = serverIndicatorToLineData(indicatorData);
          if (mainData.length === 0) return null;

          const color = SERVER_INDICATOR_COLORS[upperName] || '#888';
          const elements: React.ReactNode[] = [];

          // Main indicator line
          elements.push(
            <Series
              key={`server_${name}_main`}
              type="line"
              data={mainData}
              options={{
                color,
                title: upperName,
                lineWidth: 2,
                priceScaleId: upperName === 'RSI' ? 'rsi' : undefined,
              }}
            />
          );

          // Bollinger Bands: render upper and lower bands
          if (upperName === 'BB') {
            const upperBand = serverIndicatorMetaSeries(indicatorData, 'upperBand');
            const lowerBand = serverIndicatorMetaSeries(indicatorData, 'lowerBand');
            if (upperBand.length > 0) {
              elements.push(
                <Series
                  key={`server_${name}_upper`}
                  type="line"
                  data={upperBand}
                  options={{ color: '#CE93D8', title: 'BB Upper', lineWidth: 1 }}
                />
              );
            }
            if (lowerBand.length > 0) {
              elements.push(
                <Series
                  key={`server_${name}_lower`}
                  type="line"
                  data={lowerBand}
                  options={{ color: '#CE93D8', title: 'BB Lower', lineWidth: 1 }}
                />
              );
            }
          }

          // MACD: render signal line
          if (upperName === 'MACD') {
            const signalLine = serverIndicatorMetaSeries(indicatorData, 'signal');
            if (signalLine.length > 0) {
              elements.push(
                <Series
                  key={`server_${name}_signal`}
                  type="line"
                  data={signalLine}
                  options={{ color: '#FF5722', title: 'MACD Signal', lineWidth: 1, priceScaleId: 'macd' }}
                />
              );
            }
          }

          return <React.Fragment key={`server_${name}`}>{elements}</React.Fragment>;
        })}
      </ChartContainer>
      
      {/* Go to realtime button following chart-realtime.md pattern */}
      {showRealtimeButton && (
        <div className="absolute bottom-4 left-4">
          <button
            onClick={handleGoToRealtime}
            className="px-6 py-2 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-medium"
          >
            Go to realtime
          </button>
        </div>
      )}
    </div>
  );
};

// Helper functions for indicator calculations
function calculateMovingAverage(data: BarData[], period: number, isEMA: boolean = false): LineData[] {
  const result: LineData[] = [];
  let emaValue = 0;
  let emaInitialized = false;
  
  for (let i = 0; i < data.length; i++) {
    const time = Math.floor(new Date(data[i].barStartTime).getTime() / 1000) as Time;
    const close = data[i].close || 0; // Use OHLC value from backend
    
    if (isEMA) {
      if (!emaInitialized && i >= period - 1) {
        const sum = data.slice(i - period + 1, i + 1)
          .reduce((acc, bar) => acc + (bar.close || 0), 0);
        emaValue = sum / period;
        emaInitialized = true;
      } else if (emaInitialized) {
        const multiplier = 2 / (period + 1);
        emaValue = (close - emaValue) * multiplier + emaValue;
      }
      
      if (emaInitialized) {
        result.push({ time, value: emaValue });
      }
    } else {
      if (i >= period - 1) {
        const sum = data.slice(i - period + 1, i + 1)
          .reduce((acc, bar) => acc + (bar.close || 0), 0);
        const sma = sum / period;
        result.push({ time, value: sma });
      }
    }
  }
  
  return result;
}

function calculateRSI(data: BarData[], period: number): LineData[] {
  if (data.length < period + 1) return [];
  
  const result: LineData[] = [];
  const changes: number[] = [];
  
  for (let i = 1; i < data.length; i++) {
    const currentPrice = data[i].close || 0;
    const previousPrice = data[i - 1].close || 0;
    changes.push(currentPrice - previousPrice);
  }
  
  let avgGain = 0;
  let avgLoss = 0;
  
  for (let i = 0; i < period; i++) {
    const change = changes[i];
    if (change >= 0) {
      avgGain += change;
    } else {
      avgLoss += Math.abs(change);
    }
  }
  
  avgGain /= period;
  avgLoss /= period;
  
  let rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
  let rsi = 100 - (100 / (1 + rs));
  
  const firstTime = Math.floor(new Date(data[period].barStartTime).getTime() / 1000) as Time;
  result.push({ time: firstTime, value: rsi });
  
  for (let i = period; i < changes.length; i++) {
    const change = changes[i];
    const gain = change >= 0 ? change : 0;
    const loss = change < 0 ? Math.abs(change) : 0;
    
    avgGain = ((avgGain * (period - 1)) + gain) / period;
    avgLoss = ((avgLoss * (period - 1)) + loss) / period;
    
    rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    rsi = 100 - (100 / (1 + rs));
    
    const time = Math.floor(new Date(data[i + 1].barStartTime).getTime() / 1000) as Time;
    result.push({ time, value: Math.max(0, Math.min(100, rsi)) });
  }
  
  return result;
}

export default TradingViewChart;